// ═══════════════════════════════════════════════════════════════════════
//   ALUNO 360 — Aba Financeira (FASE 1 do Motor Financeiro Central)
//   Consome: GET /api/financial/aluno/{student_id}/dashboard
//   Fonte única: db.titles
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, CheckCircle2, Clock, AlertTriangle, TrendingUp, Receipt,
  Phone, Mail, MessageCircle, Calendar, ChevronDown, ChevronUp,
  CreditCard, Sparkles, Loader2, FileText, ExternalLink,
  Edit2, Ban, RotateCcw, Undo2, History,
} from 'lucide-react';
import ReciboUnificadoDialog from '@/pages/DnaPay/components/ReciboUnificadoDialog';
import DeclaracaoIrDialog from '@/pages/DnaPay/components/DeclaracaoIrDialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTituloActions } from '@/hooks/useTituloActions';
import { PageHeader } from '@/components/ui-dna';

const API_URL = window.location.origin;

const STATUS_STYLES = {
  pago:        { bg: 'dna-tone-success',  text: 'dna-tone-success',  border: 'dna-tone-success',  label: 'Pago' },
  aberto:      { bg: 'dna-tone-warning',  text: 'dna-tone-warning',  border: 'dna-tone-warning',     label: 'Aberto' },
  vencido:     { bg: 'dna-tone-danger',     text: 'dna-tone-danger',     border: 'dna-tone-danger',     label: 'Vencido' },
  cancelado:   { bg: 'dna-tone-neutral',   text: 'dna-tone-neutral',    border: 'dna-tone-neutral',    label: 'Cancelado' },
  renegociado: { bg: 'dna-tone-info',    text: 'dna-tone-info',    border: 'dna-tone-info',    label: 'Renegociado' },
};

const fmtBRL = (v) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    const [y, m, day] = String(d).slice(0, 10).split('-');
    return `${day}/${m}/${y}`;
  } catch { return d; }
};

const SummaryCard = ({ icon: Icon, label, value, sub, color = 'slate', testid }) => {
  const colorMap = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-500' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'text-blue-500' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    icon: 'text-rose-500' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'text-amber-500' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  icon: 'text-violet-500' },
    slate:   { bg: 'bg-slate-50',   text: 'text-slate-700',   icon: 'text-slate-500' },
  };
  const c = colorMap[color] || colorMap.slate;
  return (
    <div className={`rounded-xl border border-slate-100 ${c.bg} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${c.icon}`} />
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${c.text}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.aberto;
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${s.bg} ${s.text} border ${s.border}`}>
      {s.label}
    </span>
  );
};

const TituloRow = ({ titulo, onAction, admin }) => {
  const [expanded, setExpanded] = useState(false);
  const [historico, setHistorico] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);

  const loadHist = async () => {
    if (historico) { setHistorico(null); return; }
    setLoadingHist(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/financial/titulos/${titulo.id}/historico`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setHistorico(await res.json());
    } catch { /* noop */ }
    setLoadingHist(false);
  };

  const status = titulo.status;
  const isPaid = status === 'pago';
  const isCancelled = status === 'cancelado';
  const hasEstornoSnapshot = !!titulo.estorno_snapshot;
  const editBlocked = isCancelled || (isPaid && !admin);
  const editTitle = isCancelled
    ? 'Título cancelado não pode ser editado. Restaure-o antes.'
    : (isPaid && !admin)
      ? 'Título pago só pode ser editado por administrador. Estorne o pagamento primeiro.'
      : 'Editar título';

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`titulo-row-${titulo.id}`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 py-3 px-3 hover:bg-slate-50 transition-colors text-left"
      >
        <Receipt className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{titulo.descricao || titulo.categoria || '—'}</p>
          <p className="text-[11px] text-slate-500">
            {titulo.competencia && <>Comp. {titulo.competencia} • </>}
            Venc. {fmtDate(titulo.vencimento)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-slate-800">{fmtBRL(titulo.valor_original)}</p>
          {titulo.valor_em_aberto > 0 && titulo.status !== 'pago' && (
            <p className="text-[11px] text-rose-600">Em aberto: {fmtBRL(titulo.valor_em_aberto)}</p>
          )}
        </div>
        <StatusBadge status={titulo.status} />
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-12 pb-3 pt-1 bg-slate-50/50 space-y-2 text-xs text-slate-600">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><span className="text-slate-400">Valor pago:</span> <span className="font-semibold text-slate-700">{fmtBRL(titulo.valor_pago)}</span></div>
            <div><span className="text-slate-400">Pagamento:</span> <span className="font-semibold text-slate-700">{fmtDate(titulo.data_pagamento)}</span></div>
            <div><span className="text-slate-400">Forma:</span> <span className="font-semibold text-slate-700">{titulo.forma_pagamento || '-'}</span></div>
            <div><span className="text-slate-400">Origem:</span> <span className="font-semibold text-slate-700">{titulo.origem || 'manual'}</span></div>
          </div>

          {/* Ações de admin (FASE Auditoria) */}
          <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-200" data-testid={`acoes-${titulo.id}`}>
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => onAction('edit', titulo)} disabled={editBlocked}
              title={editTitle}
              data-testid={`btn-edit-${titulo.id}`}>
              <Edit2 className="w-3 h-3 mr-1" /> Editar
            </Button>
            {!isCancelled && !isPaid && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => onAction('cancel', titulo)}
                data-testid={`btn-cancel-${titulo.id}`}>
                <Ban className="w-3 h-3 mr-1" /> Cancelar
              </Button>
            )}
            {isCancelled && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => onAction('restore', titulo)}
                data-testid={`btn-restore-${titulo.id}`}>
                <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
              </Button>
            )}
            {isPaid && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                onClick={() => onAction('estornar', titulo)}
                data-testid={`btn-estornar-${titulo.id}`}>
                <Undo2 className="w-3 h-3 mr-1" /> Estornar pgto
              </Button>
            )}
            {hasEstornoSnapshot && !isPaid && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => onAction('reverter', titulo)}
                data-testid={`btn-reverter-${titulo.id}`}>
                <RotateCcw className="w-3 h-3 mr-1" /> Reverter estorno
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto"
              onClick={loadHist}
              data-testid={`btn-hist-${titulo.id}`}>
              <History className="w-3 h-3 mr-1" /> {historico ? 'Ocultar histórico' : 'Histórico'}
            </Button>
          </div>

          {loadingHist && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
          {historico && (
            <div className="pt-2 border-t border-slate-200">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Auditoria · {historico.total} eventos</p>
              <ul className="space-y-1 max-h-48 overflow-auto">
                {historico.eventos.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-2 text-[11px]">
                    <span className="font-mono text-slate-400">{ev.created_at?.slice(0, 19).replace('T', ' ')}</span>
                    <span className="font-bold text-violet-700">{ev.event_type}</span>
                    <span className="text-slate-600 flex-1">{ev.motivo || ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isCancelled && titulo.cancel_reason && (
            <div className="text-[11px] text-rose-600 italic">Cancelado: {titulo.cancel_reason}</div>
          )}

          {titulo.timeline?.length > 0 && (
            <div className="pt-2 border-t border-slate-200">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Timeline</p>
              <ul className="space-y-1">
                {titulo.timeline.map((ev, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    <span className="text-slate-700">{ev.label}</span>
                    <span className="text-slate-400 text-[11px]">{fmtDate(ev.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {titulo.nota_fiscal && typeof titulo.nota_fiscal === 'object' && (
            <div className="pt-2 border-t border-slate-200" data-testid={`nfse-${titulo.id}`}>
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Nota Fiscal</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border ${
                  titulo.nota_fiscal.numero
                    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                    : titulo.nota_fiscal.status === 'rejeitada'
                      ? 'text-rose-700 bg-rose-50 border-rose-200'
                      : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
                  <FileText className="w-3 h-3" />
                  {titulo.nota_fiscal.numero
                    ? `NF #${titulo.nota_fiscal.numero}`
                    : `RPS ${titulo.nota_fiscal.rps_numero || '—'}`}
                </span>
                <span className="text-[11px] text-slate-500">
                  {titulo.nota_fiscal.status}
                  {titulo.nota_fiscal.data_emissao ? ` · emitida ${fmtDate(titulo.nota_fiscal.data_emissao)}` : ''}
                  {titulo.nota_fiscal.protocolo ? ` · protocolo ${titulo.nota_fiscal.protocolo}` : ''}
                </span>
                {titulo.nota_fiscal.erro_mensagem && (
                  <span className="text-[11px] text-rose-600" data-testid={`nfse-erro-${titulo.id}`}>
                    {titulo.nota_fiscal.erro_mensagem}
                  </span>
                )}
                {titulo.nota_fiscal.pdf_url && (
                  <a href={titulo.nota_fiscal.pdf_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900 underline">
                    PDF <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {titulo.nota_fiscal.xml_url && (
                  <a href={titulo.nota_fiscal.xml_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 underline">
                    XML <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function AlunoFinanceiroTab({ studentId, year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unified, setUnified] = useState(false);
  const [irDecl, setIrDecl] = useState(false);
  const yearParam = year || new Date().getFullYear();

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/financial/aluno/${studentId}/dashboard?year=${yearParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setData(await res.json());
      } else if (res.status === 403) {
        toast.error('Sem permissão para ver financeiro deste aluno');
        setData(null);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao carregar financeiro');
        setData(null);
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [studentId, yearParam]);

  useEffect(() => { load(); }, [load]);

  const { openAction, isAdmin: admin, dialogNode } = useTituloActions({ onChanged: load });

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 flex items-center justify-center" data-testid="aluno-fin-loading">
        <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
        <span className="ml-3 text-sm text-slate-500">Carregando financeiro...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center" data-testid="aluno-fin-empty">
        <Wallet className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">Sem dados financeiros para este aluno em {yearParam}.</p>
      </div>
    );
  }

  const { resumo, responsavel_financeiro, plano_financeiro, titulos } = data;

  return (
    <div className="space-y-4" data-testid="aluno-financeiro-tab">

      {/* Header DNA Design System (compact) */}
      <div className="rounded-2xl overflow-hidden shadow-md" data-testid="aluno-fin-header">
        <PageHeader
          compact
          icon={CreditCard}
          eyebrow="Motor Financeiro Central · fonte única: títulos"
          title={`Financeiro ${yearParam}`}
          subtitle={resumo.proximo_vencimento ? `Próximo vencimento: ${fmtDate(resumo.proximo_vencimento)}` : null}
          rightSlot={
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setUnified(true)} data-testid="btn-recibo-unificado"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-white/20 hover:bg-white/30 border border-white/30 rounded-full px-3 py-1.5 transition-colors">
                <Receipt className="w-3.5 h-3.5" /> Recibo unificado
              </button>
              <button type="button" onClick={() => setIrDecl(true)} data-testid="btn-declaracao-ir"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-white/20 hover:bg-white/30 border border-white/30 rounded-full px-3 py-1.5 transition-colors">
                <FileText className="w-3.5 h-3.5" /> Declaração IR
              </button>
              {resumo.proximo_vencimento && (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-white/15 border border-white/25 rounded-full px-2.5 py-1">
                  <Calendar className="w-3 h-3" />
                  {fmtDate(resumo.proximo_vencimento)}
                </span>
              )}
            </div>
          }
        />
      </div>

      {/* Card Responsável Financeiro (FASE 2 - Boleto Safra) */}
      <ResponsavelCard responsavel={responsavel_financeiro} />

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard
          icon={Wallet} label="Total do Ano"
          value={fmtBRL(resumo.total_ano)} sub={`${resumo.qtd_titulos} títulos`}
          color="violet" testid="card-total-ano"
        />
        <SummaryCard
          icon={CheckCircle2} label="Total Pago"
          value={fmtBRL(resumo.total_pago)} sub={`${resumo.qtd_pagos} títulos`}
          color="emerald" testid="card-total-pago"
        />
        <SummaryCard
          icon={Clock} label="Total Aberto"
          value={fmtBRL(resumo.total_aberto)} sub={`${resumo.qtd_abertos} títulos`}
          color="blue" testid="card-total-aberto"
        />
        <SummaryCard
          icon={AlertTriangle} label="Total Vencido"
          value={fmtBRL(resumo.total_vencido)} sub={`${resumo.qtd_vencidos} títulos`}
          color="rose" testid="card-total-vencido"
        />
        <SummaryCard
          icon={TrendingUp} label="Inadimplência"
          value={`${resumo.inadimplencia_pct}%`}
          sub={resumo.inadimplencia_pct > 0 ? 'risco' : 'em dia'}
          color={resumo.inadimplencia_pct > 0 ? 'amber' : 'emerald'}
          testid="card-inadimplencia"
        />
      </div>

      {/* Responsável financeiro + Plano */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {responsavel_financeiro && (
          <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid="bloco-responsavel-fin">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Responsável Financeiro</p>
            <p className="text-base font-bold text-slate-800">{responsavel_financeiro.nome}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {responsavel_financeiro.email && (
                <a href={`mailto:${responsavel_financeiro.email}`}
                   className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200">
                  <Mail className="w-3 h-3" /> {responsavel_financeiro.email}
                </a>
              )}
              {responsavel_financeiro.telefone && (
                <a href={`tel:${responsavel_financeiro.telefone}`}
                   className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200">
                  <Phone className="w-3 h-3" /> {responsavel_financeiro.telefone}
                </a>
              )}
              {responsavel_financeiro.whatsapp && (
                <a href={`https://wa.me/55${(responsavel_financeiro.whatsapp || '').replace(/\D/g, '')}`}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs text-emerald-700 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 hover:bg-emerald-100">
                  <MessageCircle className="w-3 h-3" /> WhatsApp
                </a>
              )}
            </div>
          </div>
        )}

        {plano_financeiro ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid="bloco-plano-fin">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Plano Financeiro</p>
            <p className="text-base font-bold text-slate-800">{plano_financeiro.nome}</p>
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
              {plano_financeiro.mensalidade != null && (
                <div><span className="text-slate-400">Mensalidade:</span> <span className="font-semibold text-slate-700">{fmtBRL(plano_financeiro.mensalidade)}</span></div>
              )}
              {plano_financeiro.matricula != null && (
                <div><span className="text-slate-400">Matrícula:</span> <span className="font-semibold text-slate-700">{fmtBRL(plano_financeiro.matricula)}</span></div>
              )}
              {plano_financeiro.material != null && (
                <div><span className="text-slate-400">Material:</span> <span className="font-semibold text-slate-700">{fmtBRL(plano_financeiro.material)}</span></div>
              )}
              {plano_financeiro.parcelas != null && (
                <div><span className="text-slate-400">Parcelas:</span> <span className="font-semibold text-slate-700">{plano_financeiro.parcelas}x</span></div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 flex items-center justify-center text-center">
            <p className="text-xs text-slate-500">Nenhum plano financeiro vinculado.<br/>Será habilitado na FASE 2 (planos por turma).</p>
          </div>
        )}
      </div>

      {/* Lista de títulos */}
      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden" data-testid="lista-titulos">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold text-slate-800">Títulos do Aluno</span>
            <span className="text-[11px] text-slate-500">({titulos.length})</span>
          </div>
        </div>
        {titulos.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            Nenhum título encontrado em {yearParam}.
          </div>
        ) : (
          <div>
            {titulos.map(t => <TituloRow key={t.id} titulo={t} onAction={openAction} admin={admin} />)}
          </div>
        )}
      </div>

      {/* ─── Dialog de ações (hook useTituloActions) ─── */}
      {dialogNode}
      <ReciboUnificadoDialog open={unified} onOpenChange={setUnified}
        studentId={studentId} studentName={data?.aluno?.nome} year={yearParam} />
      <DeclaracaoIrDialog open={irDecl} onOpenChange={setIrDecl}
        studentId={studentId} studentName={data?.aluno?.nome} />

    </div>
  );
}

// ─────────────────────────────────────────────
// Card Responsável Financeiro (FASE 2 — Boleto Safra)
// ─────────────────────────────────────────────
const REQUIRED_BANK_FIELDS = ['cpf','zip_code','address','address_number','neighborhood','city','state'];
const FIELD_LABELS = {
  cpf: 'CPF', zip_code: 'CEP', address: 'Endereço', address_number: 'Número',
  complement: 'Complemento', neighborhood: 'Bairro', city: 'Cidade', state: 'UF',
  email: 'E-mail',
};

function ResponsavelCard({ responsavel }) {
  if (!responsavel) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3" data-testid="responsavel-card-empty">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">Sem responsável financeiro vinculado</span>
        </div>
        <p className="text-xs text-amber-800 mt-1">Vincule um responsável para emitir cobrança.</p>
      </div>
    );
  }
  const missingBank = REQUIRED_BANK_FIELDS.filter(k => !(responsavel?.[k] || '').toString().trim());
  const hasContact = !!((responsavel.phone || '').toString().trim() || (responsavel.cellphone || '').toString().trim() || (responsavel.whatsapp || '').toString().trim());
  const hasEmail = !!((responsavel.email || '').toString().trim());
  const aptBoleto = missingBank.length === 0;
  const aptNFE = aptBoleto && hasEmail && hasContact;
  const isComplete = aptBoleto && hasContact && hasEmail;

  const Pill = ({ ok, label, testid }) => (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );

  return (
    <div className={`rounded-xl border p-3 ${isComplete ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}`} data-testid="responsavel-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Responsável Financeiro</p>
          <p className="text-sm font-bold text-slate-800 mt-0.5">{responsavel.name || '—'}</p>
          <p className="text-[11px] text-slate-500">
            {responsavel.cpf ? `CPF: ${responsavel.cpf}` : 'CPF não informado'}
            {responsavel.email ? ` · ${responsavel.email}` : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Pill ok={isComplete} label={isComplete ? 'Cadastro completo' : 'Cadastro incompleto'} testid="pill-cadastro" />
          <Pill ok={aptBoleto} label={aptBoleto ? 'Apto a boleto' : 'Boleto bloqueado'} testid="pill-boleto" />
          <Pill ok={aptNFE} label={aptNFE ? 'Apto a NF-e' : 'NF-e bloqueada'} testid="pill-nfe" />
        </div>
      </div>
      {!aptBoleto && (
        <div className="mt-2 pt-2 border-t border-amber-200">
          <p className="text-[11px] text-amber-900 font-semibold">
            Para emitir boleto Safra, complete: {missingBank.map(k => FIELD_LABELS[k] || k).join(', ')}.
          </p>
        </div>
      )}
    </div>
  );
}
